import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await resetPassword(token, newPassword);
      setMessage(res.message);
    } catch (err) {
      setError(err?.response?.data?.detail || "This reset link is invalid or has expired");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white flex items-center justify-center px-6 relative overflow-hidden">
      <Helmet>
        <title>Reset Password | Skilmark</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md glass-panel rounded-3xl p-8 sm:p-10"
      >
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src="/logo.jpg" alt="Skilmark" className="w-12 h-12 rounded-lg object-cover" />
          <span className="font-display font-extrabold text-xl tracking-tight">
            SKIL<span className="text-[#7C93B8]">MARK</span>
          </span>
        </div>

        {!token ? (
          <p data-testid={AUTH.resetPasswordError} className="text-sm text-red-400 text-center">
            This reset link is missing a token. Please request a new one from the login page.
          </p>
        ) : message ? (
          <div className="text-center space-y-4">
            <p data-testid={AUTH.resetPasswordSuccessMessage} className="text-sm text-white/70">{message}</p>
            <Link
              to="/login"
              data-testid={AUTH.resetPasswordLoginLink}
              className="inline-block text-sm text-[#3B82F6] hover:text-[#60A5FA] transition-colors duration-300"
            >
              Go to log in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">New Password</Label>
              <Input
                data-testid={AUTH.resetPasswordNewInput}
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-white/5 border-white/15 text-white h-11"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Confirm Password</Label>
              <Input
                data-testid={AUTH.resetPasswordConfirmInput}
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white/5 border-white/15 text-white h-11"
              />
            </div>
            {error && (
              <p data-testid={AUTH.resetPasswordError} className="text-sm text-red-400 text-center">{error}</p>
            )}
            <button
              data-testid={AUTH.resetPasswordSubmitBtn}
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#2563EB] text-white font-semibold px-6 py-3 text-sm hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Reset Password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
